export interface ProviderLimit {
  slug: string;
  provider: string;
  officialLimit: string;
  practicalLimit: string;
  title: string;
  description: string;
  h1: string;
  summary: string;
  details: {
    maxAttachment: string;
    encodingOverhead: string;
    cloudFallback: string;
    recipientRequirement: string;
    securityLevel: string;
  };
  whyItBounces: string;
  howToBypass: string[];
  faq: { q: string; a: string }[];
}

export const PROVIDER_LIMITS: ProviderLimit[] = [
  {
    slug: "gmail-attachment-size-limit",
    provider: "Gmail (Google)",
    officialLimit: "25 MB",
    practicalLimit: "≈ 18.5 MB",
    title: "Gmail Attachment Size Limit (2026) — How to Send Larger Files",
    description: "Gmail caps attachments at 25 MB (≈18.5 MB practical). Learn why emails bounce and how to send up to 3 GB files free with end-to-end encryption.",
    h1: "Gmail Attachment Size Limit & How to Send Larger Files",
    summary: "Google Gmail rejects any email message exceeding 25 megabytes in total MIME size. Because binary attachments require Base64 encoding (which adds 33 % overhead), files larger than approximately 18.5 MB will fail to send.",
    details: {
      maxAttachment: "25 MB (entire message, headers + body + files)",
      encodingOverhead: "Base64 encoding increases file size by 33%",
      cloudFallback: "Forces Google Drive upload (requires recipient to have Google access)",
      recipientRequirement: "Recipient email server must also accept 25 MB",
      securityLevel: "Stored in plaintext on Google servers; scanned for advertising & spam",
    },
    whyItBounces: "When you attach a file in Gmail, your browser translates raw binary bytes into ASCII text (Base64). A 20 MB MP4 or PDF becomes a 26.6 MB payload. Gmail checks the encoded message size against its 25 MB ceiling and rejects the delivery or forces you to upload it to Google Drive.",
    howToBypass: [
      "Do not send the raw file as an email attachment — send an encrypted LimeShare link instead.",
      "LimeShare encrypts files up to 3 GB in your browser with AES-256-GCM before upload.",
      "Your recipient gets full-speed download without needing a Google account or requesting drive permissions.",
      "The link automatically self-destructs after 48 hours or upon first download.",
    ],
    faq: [
      {
        q: "What is Gmail's exact attachment limit in 2026?",
        a: "Gmail allows a maximum message size of 25 MB for sending and 50 MB for receiving. However, because attachments are Base64 encoded, any single file larger than ~18.5 MB will trigger Gmail's Google Drive prompt.",
      },
      {
        q: "Why is Google Drive not ideal for sending confidential files?",
        a: "Google Drive retains your files indefinitely, indexes plaintext content, and often prompts recipients for Google login or permission requests. LimeShare provides zero-knowledge encryption and auto-purging.",
      },
    ],
  },
  {
    slug: "outlook-attachment-size-limit",
    provider: "Outlook & Microsoft 365",
    officialLimit: "20 MB – 34 MB",
    practicalLimit: "≈ 15 MB – 25 MB",
    title: "Outlook Attachment Size Limit 2026: Bypass the 20MB Cap",
    description: "Outlook and Exchange limit attachments to 20MB. Learn why Outlook says 'The file you're attaching is bigger than the server allows' and how to send large files.",
    h1: "Outlook & Exchange Attachment Size Limits Explained",
    summary: "Microsoft Outlook and Office 365 default to a 20 MB attachment cap for standard accounts and 34 MB for Exchange Online. Attempting to attach larger files triggers the infamous 'The file you're attaching is bigger than the server allows' error.",
    details: {
      maxAttachment: "20 MB (free Outlook.com / Hotmail), 34 MB (Microsoft 365 Enterprise)",
      encodingOverhead: "Exchange MIME encoding adds 33% bloat",
      cloudFallback: "Prompts to share via OneDrive link",
      recipientRequirement: "External Exchange servers often cap at 10 MB or 20 MB",
      securityLevel: "Scanned by Microsoft Defender; unencrypted at rest on recipient server",
    },
    whyItBounces: "Enterprise mail administrators routinely set maximum incoming message sizes between 10 MB and 25 MB to prevent mailbox bloat and DDoS. Even if your corporate Outlook allows sending 34 MB, the recipient's mail server will bounce it back with error 552 5.3.4 (message size exceeds limit).",
    howToBypass: [
      "Use LimeShare to generate an end-to-end encrypted transfer link up to 3 GB.",
      "Paste the link into your Outlook email body — it avoids all mail server attachment limits.",
      "Set a password (Argon2id) and send it via Microsoft Teams or SMS for dual-channel enterprise security.",
    ],
    faq: [
      {
        q: "Why does Outlook say 'The file you're attaching is bigger than the server allows'?",
        a: "Your Exchange administrator has set a maximum attachment size rule (typically 20 MB). Files larger than this cannot be attached directly to the message.",
      },
      {
        q: "How can I email a 1 GB file through Outlook?",
        a: "You cannot attach a 1 GB file directly. Use LimeShare to encrypt the 1 GB file client-side and paste the self-destructing link into your Outlook email.",
      },
    ],
  },
  {
    slug: "whatsapp-file-size-limit",
    provider: "WhatsApp",
    officialLimit: "2 GB (Documents) / 16 MB (Media)",
    practicalLimit: "2 GB (document) with quality loss for video",
    title: "WhatsApp File Size Limit 2026: Send Full-Quality Videos & Files",
    description: "WhatsApp limits video to 16MB and documents to 2GB while compressing photos. Learn how to send uncompressed 4K video and large files via WhatsApp.",
    h1: "WhatsApp File Size Limits & Quality Loss Guide",
    summary: "WhatsApp supports files up to 2 GB when sent as 'Documents', but standard video and photo attachments are capped at 16 MB and heavily re-compressed to 720p or low-bitrate 1080p.",
    details: {
      maxAttachment: "16 MB for standard media, 2 GB for document picker",
      encodingOverhead: "Aggressive lossy re-compression for videos and photos",
      cloudFallback: "None — upload fails directly in chat",
      recipientRequirement: "Requires both parties on WhatsApp with phone numbers exposed",
      securityLevel: "E2EE in transit, but backups (Google Drive / iCloud) are often unencrypted",
    },
    whyItBounces: "If you share an iPhone 4K 60fps video via WhatsApp's media gallery, WhatsApp recompresses it from 50 Mbps down to ~2 Mbps, stripping HDR10, Rec.2020 color, and 60fps smoothness.",
    howToBypass: [
      "Upload your raw 4K video or photo archive to LimeShare in your mobile browser.",
      "Copy the sealed link and send it directly in your WhatsApp chat.",
      "The recipient downloads the bit-for-bit pristine file without any WhatsApp re-compression or phone number leakage.",
    ],
    faq: [
      {
        q: "Why does WhatsApp compress my 4K video to low quality?",
        a: "WhatsApp optimizes for data bandwidth and chat speed on mobile networks. It downsamples media sent through the photo gallery. Sending via LimeShare preserves 100% original quality.",
      },
      {
        q: "Can I send files over 2 GB to WhatsApp contacts?",
        a: "Yes. LimeShare supports files up to 3 GB free (and 50 GB on Pro), generating a link you can paste into any WhatsApp conversation.",
      },
    ],
  },
  {
    slug: "telegram-file-size-limit",
    provider: "Telegram",
    officialLimit: "2 GB (Free) / 4 GB (Premium)",
    practicalLimit: "2 GB / 4 GB (slow upload speeds on free)",
    title: "Telegram File Size Limit 2026: 2GB vs 4GB & Faster Alternatives",
    description: "Telegram limits uploads to 2GB for free users and 4GB for Premium. Learn about download throttling and how to send up to 3GB with zero logs.",
    h1: "Telegram Upload Limits & Download Throttling",
    summary: "Telegram is famous for generous file sizes (2 GB free, 4 GB with Telegram Premium), but free users frequently face throttled download speeds and permanent cloud storage on Telegram's servers.",
    details: {
      maxAttachment: "2 GB (Free), 4 GB (Telegram Premium $4.99/mo)",
      encodingOverhead: "None (binary stream)",
      cloudFallback: "Files stay permanently in chat cloud",
      recipientRequirement: "Both parties must use Telegram and be connected",
      securityLevel: "Not end-to-end encrypted in group chats or standard chats (stored on MTProto cloud)",
    },
    whyItBounces: "Standard Telegram chats do not use end-to-end encryption — files are stored on Telegram's cloud clusters indefinitely unless manually deleted. Furthermore, non-Premium download speeds are often capped below broadband capacity.",
    howToBypass: [
      "Use LimeShare for true zero-knowledge transfers: files are encrypted client-side with AES-256-GCM.",
      "LimeShare links auto-destruct after 48 hours or on first download, leaving zero trace in permanent cloud chats.",
      "Zero-egress edge storage provides full unthrottled gigabit download speeds for free.",
    ],
    faq: [
      {
        q: "Are files sent on Telegram private?",
        a: "Standard Telegram files are stored on Telegram's cloud servers where Telegram holds the decryption keys. LimeShare provides true client-side zero-knowledge encryption.",
      },
    ],
  },
  {
    slug: "discord-file-size-limit",
    provider: "Discord",
    officialLimit: "10 MB (Free) / 500 MB (Nitro)",
    practicalLimit: "10 MB",
    title: "Discord File Size Limit 2026: How to Bypass the 10MB Cap",
    description: "Discord lowered its upload limit to 10MB. Learn why files fail on Discord and how to share clips, mods, and archives up to 3GB for free.",
    h1: "Discord 10MB File Limit: How to Send Large Clips & Mods",
    summary: "In late 2023, Discord reduced the free upload ceiling from 25 MB down to just 10 MB for standard users, requiring a $9.99/month Nitro subscription for 500 MB files. Even a short 1080p gaming clip routinely exceeds 10 MB.",
    details: {
      maxAttachment: "10 MB (Free users), 500 MB (Discord Nitro)",
      encodingOverhead: "Re-compressed preview generated by Discord CDN",
      cloudFallback: "Upload rejected with 'Your files are too powerful'",
      recipientRequirement: "Recipient must be on the same Discord server or DM",
      securityLevel: "Publicly accessible via Discord CDN links (expiring signed URLs)",
    },
    whyItBounces: "A 30-second 1080p 60fps shadowplay clip is roughly 40–80 MB. Discord blocks it instantly with a paywall prompting you to subscribe to Nitro.",
    howToBypass: [
      "Drop your clip or mod file into LimeShare in your browser.",
      "Get an instant encrypted link (up to 3 GB free).",
      "Paste the link into your Discord channel or DM — no Nitro required.",
    ],
    faq: [
      {
        q: "Why did Discord lower the upload limit to 10 MB?",
        a: "Discord reduced free file limits to cut storage infrastructure costs and push users toward Nitro subscriptions.",
      },
      {
        q: "Can I share game mods and game builds via LimeShare on Discord?",
        a: "Yes. LimeShare supports files up to 3 GB free with no executable file restrictions or virus false-positives.",
      },
    ],
  },
  {
    slug: "icloud-mail-drop-limit",
    provider: "Apple iCloud / Mail Drop",
    officialLimit: "5 GB (Mail Drop) / 20 MB (Direct)",
    practicalLimit: "5 GB (expires in 30 days, slow on non-Apple devices)",
    title: "Apple Mail Drop & iCloud Attachment Limits 2026",
    description: "Apple Mail caps direct attachments at 20MB and Mail Drop at 5GB for 30 days. Learn how to send large files from iPhone and Mac with faster self-destructing links.",
    h1: "Apple Mail & iCloud Mail Drop Attachment Limits",
    summary: "Apple Mail allows attachments up to 20 MB directly. For larger files, it invokes 'Mail Drop', which routes through iCloud up to 5 GB. However, Mail Drop links linger for 30 days and lack client-side encryption.",
    details: {
      maxAttachment: "20 MB direct, 5 GB via iCloud Mail Drop",
      encodingOverhead: "iCloud sync delay before link becomes available",
      cloudFallback: "Requires recipient to download from iCloud web",
      recipientRequirement: "Any recipient with browser",
      securityLevel: "Encrypted at rest with Apple holding keys; no password protection",
    },
    whyItBounces: "Non-Apple recipients often experience slow iCloud web download speeds, and senders cannot customize the 30-day expiration window or set passwords.",
    howToBypass: [
      "Use LimeShare on iPhone (Safari) or Mac to encrypt files client-side with AES-256.",
      "Choose your own retention time: 1 hour, 6 hours, 24 hours, or 48 hours.",
      "Enable 'Burn after first download' so your personal files don't stay online for 30 days.",
    ],
    faq: [
      {
        q: "How long do Apple Mail Drop links last?",
        a: "Mail Drop links expire after 30 days and cannot be deleted early. LimeShare gives you control to expire links in 1 hour or burn on read.",
      },
    ],
  },
  {
    slug: "slack-file-size-limit",
    provider: "Slack",
    officialLimit: "1 GB",
    practicalLimit: "1 GB (counts against workspace storage quotas)",
    title: "Slack File Upload Limit 2026 & Workspace Storage Caps",
    description: "Slack limits file uploads to 1GB per file and enforces workspace storage limits (5GB on free). Learn how to share large work files without eating storage.",
    h1: "Slack File Upload Limit & Workspace Quota Management",
    summary: "Slack allows individual file uploads up to 1 GB, but free workspaces have a tight 5 GB total storage limit. Once exceeded, older messages and files are hidden behind paywalls.",
    details: {
      maxAttachment: "1 GB per file",
      encodingOverhead: "Files count permanently toward workspace storage limits",
      cloudFallback: "Upload fails when workspace total storage is full",
      recipientRequirement: "Requires Slack workspace membership",
      securityLevel: "Stored in plaintext for Slack administrators; subject to corporate retention",
    },
    whyItBounces: "Uploading design prototypes, raw screen recordings, or database dumps quickly exhausts the shared workspace storage pool, forcing teams into paid Slack plans.",
    howToBypass: [
      "Share large files via LimeShare links inside your Slack channels.",
      "Your Slack storage quota remains at 0 MB because files live on zero-knowledge cloud storage.",
      "Client-side encryption ensures confidential internal documents remain unreadable to Slack or third-party app integrations.",
    ],
    faq: [
      {
        q: "Do LimeShare links consume my Slack workspace storage?",
        a: "No. Only the text link is posted in Slack. The file bytes stream directly from encrypted edge storage and self-destruct, saving your workspace storage quota.",
      },
    ],
  },
  {
    slug: "yahoo-mail-attachment-limit",
    provider: "Yahoo Mail",
    officialLimit: "25 MB",
    practicalLimit: "≈ 18 MB",
    title: "Yahoo Mail Attachment Size Limit 2026 & How to Send Big Files",
    description: "Yahoo Mail caps attachments at 25MB (18MB practical). Learn why Yahoo rejects big attachments and how to send up to 3GB files free.",
    h1: "Yahoo Mail Attachment Size Limit (2026 Guide)",
    summary: "Yahoo Mail enforces a strict 25 MB message cap. After Base64 encoding, any file larger than 18 MB will trigger an error preventing email delivery.",
    details: {
      maxAttachment: "25 MB (entire email combined)",
      encodingOverhead: "33% MIME encoding expansion",
      cloudFallback: "Prompts for Dropbox integration",
      recipientRequirement: "Recipient email must accept 25 MB",
      securityLevel: "Scanned by Yahoo advertising algorithms; unencrypted at rest",
    },
    whyItBounces: "Yahoo Mail scans message contents and attachments for advertising targeting and security, rejecting attachments above 25 MB before the message can be sent.",
    howToBypass: [
      "Encrypt your file with LimeShare in your browser (up to 3 GB free).",
      "Paste the resulting link into your Yahoo Mail draft.",
      "Your recipient clicks the link and downloads the file instantly with no Yahoo sign-in required.",
    ],
    faq: [
      {
        q: "What is Yahoo Mail's maximum file size?",
        a: "25 MB for the entire email. Individual files should be kept under 18 MB to account for MIME encoding.",
      },
    ],
  },
  {
    slug: "signal-file-size-limit",
    provider: "Signal Messenger",
    officialLimit: "100 MB",
    practicalLimit: "100 MB",
    title: "Signal File Size Limit 2026: Send Files Larger than 100MB",
    description: "Signal caps attachments at 100MB. Learn how to send multi-gigabyte files to Signal contacts while keeping zero-knowledge end-to-end encryption.",
    h1: "Signal 100MB File Limit & How to Send Larger Files",
    summary: "Signal is the gold standard for private messaging, but enforces a strict 100 MB attachment cap to protect server infrastructure. Attempting to share large 4K video clips or backups fails.",
    details: {
      maxAttachment: "100 MB per attachment",
      encodingOverhead: "Binary encrypted payload",
      cloudFallback: "Upload fails directly in chat",
      recipientRequirement: "Both parties on Signal",
      securityLevel: "Signal Protocol E2EE (no third-party cloud access)",
    },
    whyItBounces: "Signal's non-profit infrastructure cannot store or relay gigabytes of media per message, capping files at 100 MB.",
    howToBypass: [
      "Upload your file to LimeShare: it is encrypted client-side with AES-256-GCM before upload.",
      "Copy the sealed link and send it inside your encrypted Signal chat.",
      "The encryption key lives in the URL fragment (#) and is protected by Signal's end-to-end encrypted transport.",
    ],
    faq: [
      {
        q: "Does sending LimeShare links in Signal preserve privacy?",
        a: "Yes. The decryption key stays in the URL hash, which Signal transmits encrypted to your contact. Neither our servers nor network intermediaries can read the file.",
      },
    ],
  },
  {
    slug: "proton-mail-attachment-limit",
    provider: "Proton Mail",
    officialLimit: "25 MB",
    practicalLimit: "≈ 19 MB",
    title: "Proton Mail Attachment Size Limit (2026) — Send Up to 3GB",
    description: "Proton Mail limits attachments to 25MB. Learn how to send large confidential files to Proton Mail users with zero-knowledge encryption.",
    h1: "Proton Mail 25MB Attachment Limit Guide",
    summary: "Proton Mail provides end-to-end encrypted email, but message attachments are capped at 25 MB (≈19 MB practical after MIME encoding).",
    details: {
      maxAttachment: "25 MB per message",
      encodingOverhead: "33% Base64 expansion",
      cloudFallback: "Requires Proton Drive account",
      recipientRequirement: "Any recipient",
      securityLevel: "PGP encrypted at rest",
    },
    whyItBounces: "Encrypted PGP emails still travel over standard SMTP protocols, subject to the universal 25 MB gateway limit.",
    howToBypass: [
      "Encrypt files up to 3 GB with LimeShare in your browser.",
      "Paste the resulting link into your Proton Mail draft.",
      "Add an Argon2id password and share it via encrypted Proton Mail or Signal.",
    ],
    faq: [
      {
        q: "How can I send a 2 GB file via Proton Mail?",
        a: "Use LimeShare to generate an encrypted self-destructing link and paste it into your Proton Mail message.",
      },
    ],
  },
];
